import PusherJS, { Channel } from "pusher-js";

import Api from "js/core/api";
import { isDevelopment, pusherKey, pusherCluster, disablePusher } from "js/config";
import getLogger, { LogGroup } from "js/core/logger";

const logger = getLogger(LogGroup.PUSHER);

const PUSHER_ERROR_TYPE = "PusherError";

// Uncomment to enable pusher events logging
// PusherJS.logToConsole = true;

export interface ExtendedChannel extends Channel {
    isInUse: boolean;
}

class Pusher {
    private _pusherClient: PusherJS = null;

    constructor() {
        if (disablePusher) {
            return;
        }

        this._pusherClient = new PusherJS(pusherKey, {
            cluster: pusherCluster,
            authorizer: channel => ({
                authorize: (socketId, callback) => {
                    const channelId = channel.name;

                    // Won't track offline status for pusher auth requests
                    Api.pusherAuthenticate.post({ channelId, socketId }, null, false, false, false)
                        .then(authData => callback(null, authData))
                        .catch(err => callback(err, { auth: "" }));
                }
            })
        });

        this._pusherClient.connection.bind("error", err => {
            // In some cases pusher puts the actual pusher error object
            // into the 'error' subfield
            let pusherError;
            if (err.type === PUSHER_ERROR_TYPE) {
                pusherError = err;
            } else if (err.error?.type === PUSHER_ERROR_TYPE) {
                pusherError = err.error;
            }

            if (pusherError && (
                (pusherError.data.code >= 4200 && pusherError.data.code <= 4299) ||
                pusherError.data.code === 1006
            )) {
                // Non critical errors upon which the connection will be re-established
                logger.warn("[pusher] connnection error", pusherError);
            } else {
                logger.error(err, "[pusher] critical connnection error", err);
            }
        });
    }

    private _ensureSubscribed(channel: Channel) {
        if (channel.subscribed) {
            // Already subscribed
            return Promise.resolve();
        }

        return new Promise<void>((resolve, reject) => {
            const onError = err => {
                channel.unbind("pusher:subscription_error", onError);
                channel.unbind("pusher:subscription_succeeded", onSuccess);
                reject(err);
            };
            const onSuccess = () => {
                channel.unbind("pusher:subscription_error", onError);
                channel.unbind("pusher:subscription_succeeded", onSuccess);
                resolve();
            };
            channel.bind("pusher:subscription_error", onError);
            channel.bind("pusher:subscription_succeeded", onSuccess);
        });
    }

    public async subscribe(channelId: string) {
        if (!this._pusherClient) {
            return null;
        }

        // Subscribing to channel
        const channel = this._pusherClient.subscribe(channelId);

        // Allows to know if something else is using the channel
        if (!channel.hasOwnProperty("isInUse")) {
            Object.defineProperty(channel, "isInUse", {
                get() {
                    const channel: Channel = this; // Casting
                    return Object.keys(channel.callbacks._callbacks).length > 0 || channel.subscriptionPending;
                }
            });
        }

        // Waiting for subscription to establish
        await this._ensureSubscribed(channel);

        return channel as ExtendedChannel;
    }

    public unsubscribe(channelId: string) {
        if (!this._pusherClient) {
            return;
        }

        this._pusherClient.unsubscribe(channelId);
    }

    public async waitForEvent<ResponseType>(channelId: string, successEventType: string, errorEventType: string = null, timeoutMs: number = null) {
        const channel = await this.subscribe(channelId);
        if (!channel) {
            return null;
        }

        return new Promise<ResponseType>((resolve, reject) => {
            let timeoutHandler;
            const pusherCallbacks: Record<string, Function> = {};

            const unsubscribe = () => {
                Object.entries(pusherCallbacks).forEach(([eventName, callback]) => {
                    channel.unbind(eventName, callback);
                });
                if (!channel.isInUse) {
                    channel.unsubscribe();
                }
            };

            pusherCallbacks[successEventType] = response => {
                unsubscribe();
                if (timeoutHandler) clearTimeout(timeoutHandler);
                resolve(response as ResponseType);
            };

            if (errorEventType) {
                pusherCallbacks[errorEventType] = response => {
                    unsubscribe();
                    if (timeoutHandler) clearTimeout(timeoutHandler);
                    reject(new Error(response.errorMessage));
                };
            }

            if (timeoutMs) {
                timeoutHandler = setTimeout(() => {
                    unsubscribe();
                    reject(new Error(`Failed to receive event for channel id ${channelId}, timed out after ${timeoutMs}ms`));
                }, timeoutMs);
            }

            Object.entries(pusherCallbacks).forEach(([eventName, callback]) => {
                channel.bind(eventName, callback);
            });
        });
    }
}

const pusher = new Pusher();

if (isDevelopment) {
    // For local debugging
    (window as any).pusher = pusher;
}

export default pusher;
